import { gql, useQuery } from '@apollo/client';
import { Alert, Link } from '@apollo/orbit';
import moment from 'moment';
import React, { useCallback } from 'react';

import { ContactSupportLink } from 'src/components/ContactSupportCTAs';
import { useCurrentAccountId } from 'src/hooks/useCurrentAccountId';
import { usePulsarCommercialEntitlementsBanners } from 'src/lib/featureFlags';
import {
  EnterpriseEntitlementsExpiry,
  EnterpriseEntitlementsExpiryVariables,
} from 'src/lib/graphqlTypes/types';
import { DocsRouter } from 'src/lib/routers';

export const enterpriseDaysUntilExpiration = gql<
  EnterpriseEntitlementsExpiry,
  EnterpriseEntitlementsExpiryVariables
>`
  query EnterpriseEntitlementsExpiry($accountId: ID!) {
    account(id: $accountId) {
      id
      routerEntitlement {
        warnAt
        haltAt
      }
    }
  }
`;

interface ExpirationBannerProps {
  dateToDisplay: string;
  bannerType: 'warning' | 'error';
}

const SubscriptionExpirationBanner = ({
  dateToDisplay,
  bannerType,
}: ExpirationBannerProps) => (
  <Alert className="mb-4" status={bannerType}>
    Your enterprise subscription has expired.{' '}
    <Link to={DocsRouter.path('Router')} isExternal>
      If GraphOS Enterprise router features are in use
    </Link>
    , your router will stop serving requests as of {dateToDisplay}.{' '}
    <ContactSupportLink>
      Please contact us for billing support.
    </ContactSupportLink>
  </Alert>
);

export const EnterpriseExpirationBanners = () => {
  const showCommercialEntitlementsBanners =
    usePulsarCommercialEntitlementsBanners();
  const [accId] = useCurrentAccountId();

  const skip = !showCommercialEntitlementsBanners || !accId;
  const { data, loading } = useQuery(enterpriseDaysUntilExpiration, {
    variables: { accountId: accId as string },
    skip,
    skipPollAttempt: useCallback(() => skip, [skip]),
  });

  // warnAt is in the past => expiration expired, currently in grace period.
  const warnAt = data?.account?.routerEntitlement?.warnAt;
  const haltAt = data?.account?.routerEntitlement?.haltAt;
  const shouldWarn = moment().isAfter(moment(warnAt), 'minutes');

  if (!loading && shouldWarn && showCommercialEntitlementsBanners && haltAt) {
    // haltAt is in the past => router is off, show error banner.
    const dateToDisplay = moment(haltAt).format('MMMM Do, YYYY');
    const shouldShowError = moment().isAfter(moment(haltAt), 'minutes');

    return (
      <SubscriptionExpirationBanner
        dateToDisplay={dateToDisplay}
        bannerType={shouldShowError ? 'error' : 'warning'}
      />
    );
  } else {
    return null;
  }
};
